import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, Package, Building2, CheckCircle2, ShieldCheck, 
  CreditCard, Heart, Loader2, Plus, X, Pencil,
  Home, Briefcase
} from "lucide-react";
import toast from "react-hot-toast";
import SummaryApi from "../common";

// --- Reusable Input Component ---
const FormInput = ({ label, value, onChange, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-black text-purple-400 uppercase tracking-widest ml-1 text-left">
      {label}
    </label>
    <input
      {...props}
      className="w-full bg-purple-50/30 border border-purple-100 rounded-xl p-3 text-sm text-purple-950 font-bold outline-none focus:ring-2 ring-pink-200 transition-all"
      value={value || ""} 
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const SakhiCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedTier, userDetails, isAnonymous } = location.state || {};

  const [distributionType, setDistributionType] = useState("eveya");
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [typeError, setTypeError] = useState("");
  const [editMode, setEditMode] = useState(false);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({
    user_name: "", user_mobile: "", pincode: "", flat_number: "",
    street_name: "", land_mark: "", city: "", state: "", address: "", address_type: ""
  });

  const getUserId = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      const user = JSON.parse(sessionData);
      return user?.user_id || user?._id || user?.id;
    } catch (err) { return null; }
  }, []);

  const handleTypeChange = (val) => {
    setAddressForm((prev) => ({ ...prev, address_type: val }));
    const isDuplicate = addresses.some(
      (addr) => addr.address_type?.toLowerCase() === val.toLowerCase() && addr.address_id !== addressForm.address_id
    );
    if (isDuplicate && val.trim() !== "") {
      setTypeError("address name is already used. Please choose another");
    } else {
      setTypeError("");
    }
  };

  const fetchUserAddresses = useCallback(async () => {
    const userId = getUserId();
    if (!userId || isAnonymous || distributionType === "eveya") return;
    try {
      const res = await fetch(SummaryApi.getUserAddressbyId.url, {
        method: SummaryApi.getUserAddressbyId.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      if (result.status && Array.isArray(result.data)) {
        setAddresses(result.data);
        const def = result.data.find((a) => a.is_default === 1) || result.data[0];
        if (def) setSelectedAddressId(def.address_id);
      }
    } catch (err) { console.error("Address error", err); }
  }, [getUserId, isAnonymous, distributionType]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!selectedTier) navigate("/sponsorship");
    fetchUserAddresses();
  }, [selectedTier, navigate, fetchUserAddresses]);

  // FIX: Robust mapping of landmark field when entering edit mode
  const handleEditClick = (e, addr) => {
    e.stopPropagation();
    setEditMode(true);
    const existingLandmark = addr.land_mark || addr.landmark || "";
    setAddressForm({ 
        ...addr,
        land_mark: existingLandmark 
    });
    setShowAddressForm(true);
    setTypeError("");
  };

  const handleAddNewClick = () => {
    setEditMode(false);
    setAddressForm({
      user_name: "", user_mobile: "", pincode: "", flat_number: "",
      street_name: "", land_mark: "", city: "", state: "", address: "", address_type: ""
    });
    setShowAddressForm(true);
    setTypeError("");
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    if (typeError) return;

    const userId = getUserId();
    const apiConfig = editMode ? SummaryApi.addressUpdate : SummaryApi.addressCreate;

    // FIX: Send both naming variants to the API to ensure backend catches it
    const payload = {
        ...addressForm,
        user_id: userId,
        landmark: addressForm.land_mark 
    };

    try {
      const response = await fetch(apiConfig.url, {
        method: apiConfig.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.status) {
        toast.success(editMode ? "Address updated" : "New address added");
        fetchUserAddresses();
        setShowAddressForm(false);
        setEditMode(false);
      }
    } catch (err) { toast.error("Could not save address"); }
  };

  const handleSakhiOrderCreate = async () => {
    setIsSubmitting(true);
    const userId = getUserId();
    const finalUserId = isAnonymous ? 0 : (userId || 0);
    const finalUserName = isAnonymous ? "Anonymous" : userDetails.name;
    const finalMobile = isAnonymous ? "" : userDetails.mobile;
    
    let finalOrderAddress = "";
    if (distributionType === "eveya") {
        finalOrderAddress = "Eveya Managed Rural Distribution";
    } else if (isAnonymous) {
        finalOrderAddress = "Anonymous Contribution - Self Distributed";
    } else {
        const addr = addresses.find(a => a.address_id === selectedAddressId);
        if (addr) {
            const landmarkVal = addr.land_mark || addr.landmark || "";
            const landmarkPart = landmarkVal ? `Ref: ${landmarkVal}, ` : "";
            finalOrderAddress = `${addr.flat_number}, ${addr.street_name}, ${landmarkPart}${addr.city} - ${addr.pincode}`;
        } else {
            finalOrderAddress = "Self Distributed";
        }
    }

    const numericAmount = selectedTier?.price.replace(/[^0-9]/g, "");
    const orderPayload = {
      user_id: finalUserId,
      user_name: finalUserName,
      mobile_number: finalMobile,
      order_address: finalOrderAddress,
      order_amount: numericAmount,
      plan_name: selectedTier?.tier,
      pads_count: selectedTier?.pads,
      distribution_method: distributionType,
      is_anonymous: isAnonymous ? 1 : 0,
      delivery_charges: "0",
      total_amount: parseInt(numericAmount),
      payment_mode: "Online",
      is_sakhi: 1,
      sakhi_through_mode: distributionType,
    };

    const loadingToast = toast.loading("Confirming your contribution...");

    try {
      const response = await fetch(SummaryApi.SakhiOrderCreate.url, {
        method: SummaryApi.SakhiOrderCreate.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderPayload),
      });
      const dataResponse = await response.json();
      if (dataResponse.status === true) {
        toast.dismiss(loadingToast);
         const certificateData = {
    userName: isAnonymous ? "Anonymous Benefactor" : userDetails.name,
    planName: selectedTier?.tier,
    padsCount: selectedTier?.pads,
    date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
    transactionId: dataResponse.data?.order_id || "SAKHI-" + Math.floor(Math.random() * 100000)
  };
        if (dataResponse.data?.payment_url) {
          window.location.href = dataResponse.data.payment_url;
        } else {
          setTimeout(() => navigate("/my-orders", { state: { certificateData } }), 1000);
        }
      }
    } catch (error) { toast.error("Network error.", { id: loadingToast }); }
    finally { setIsSubmitting(false); }
  };

  if (!selectedTier) return null;

  return (
    <div className="bg-[#FFF0F3] min-h-screen font-sans pb-20">
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => { setShowModal(false); setTypeError(""); }} className="absolute inset-0 bg-purple-950/60 backdrop-blur-md" />
            <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }} className="relative bg-white rounded-[2.5rem] w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-black text-purple-950 uppercase tracking-tight">Checkout Confirmation</h2>
                <button onClick={() => { setShowModal(false); setTypeError(""); }} className="text-gray-400 hover:text-purple-900"><X size={24}/></button>
              </div>

              <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                <div className="mb-8">
                    <h4 className="text-sm font-black text-purple-950 uppercase mb-4">Distribution Address</h4>
                    
                    {distributionType === "eveya" ? (
                        <div className="p-6 rounded-3xl border-2 border-purple-800 bg-purple-50 flex items-center gap-4">
                            <div className="w-12 h-12 bg-purple-800 rounded-2xl flex items-center justify-center text-white shrink-0">
                                <Building2 size={24} />
                            </div>
                            <div>
                                <p className="font-black text-purple-950 uppercase text-sm">Eveya Managed</p>
                                <p className="text-xs text-purple-800/70 font-bold uppercase">Rural Beneficiaries Distribution</p>
                            </div>
                            <CheckCircle2 className="ml-auto text-purple-800" size={24} />
                        </div>
                    ) : (
                        !isAnonymous ? (
                            !showAddressForm ? (
                                <div className="space-y-3">
                                    {addresses.map((addr) => (
                                    <div key={addr.address_id} onClick={() => setSelectedAddressId(addr.address_id)} className={`relative p-5 rounded-[2rem] border-2 cursor-pointer transition-all flex items-start gap-4 ${selectedAddressId === addr.address_id ? "border-pink-500 bg-pink-50" : "border-gray-100 bg-white"}`}>
                                        <div className={selectedAddressId === addr.address_id ? "text-pink-500 mt-1" : "text-gray-300 mt-1"}>
                                            <CheckCircle2 size={24} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start">
                                                <p className="font-black text-purple-950 uppercase text-xs">{addr.user_name}</p>
                                                <div className="flex items-center gap-2">
                                                  <div className="px-2 py-0.5 rounded-full flex items-center gap-1 bg-purple-50 text-purple-600">
                                                    <span className="text-[8px] font-black uppercase">{addr.address_type}</span>
                                                  </div>
                                                  <button 
                                                    onClick={(e) => handleEditClick(e, addr)}
                                                    className="p-1.5 text-purple-300 hover:text-purple-600 transition-colors"
                                                  >
                                                    <Pencil size={16} />
                                                  </button>
                                                </div>
                                            </div>
                                            <p className="text-[11px] text-gray-500 font-medium leading-relaxed mt-1">
                                                {addr.flat_number}, {addr.street_name}, 
                                                {(addr.land_mark || addr.landmark) && <span className="text-gray-500 font-medium"> {addr.land_mark || addr.landmark},</span>}
                                                {" "}{addr.city} - {addr.pincode}
                                            </p>
                                        </div>
                                    </div>
                                    ))}
                                    <button onClick={handleAddNewClick} className="w-full py-4 border-2 border-dashed border-purple-200 rounded-3xl text-purple-600 font-black text-sm hover:bg-purple-50 uppercase">+ Add New Address</button>
                                </div>
                            ) : (
                                <form onSubmit={handleAddressSubmit} className="space-y-4 bg-purple-50/20 p-6 rounded-[2.5rem] border border-purple-100">
                                    <div className="flex items-center justify-between mb-2">
                                      <h4 className="text-xs font-black text-purple-950 uppercase">{editMode ? "Edit Address" : "New Address Details"}</h4>
                                      <button type="button" onClick={() => setShowAddressForm(false)} className="text-purple-400 text-[10px] font-black uppercase underline">Back</button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <FormInput label="Full Name" value={addressForm.user_name} onChange={(v) => setAddressForm({...addressForm, user_name: v})} required />
                                      <FormInput label="Mobile" maxLength={10} value={addressForm.user_mobile} onChange={(v) => setAddressForm({...addressForm, user_mobile: v.replace(/\D/g,"")})} required />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <FormInput label="Flat / House No" value={addressForm.flat_number} onChange={(v) => setAddressForm({...addressForm, flat_number: v})} required />
                                      <FormInput label="Street Name" value={addressForm.street_name} onChange={(v) => setAddressForm({...addressForm, street_name: v})} required />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <FormInput label="Landmark" value={addressForm.land_mark} onChange={(v) => setAddressForm({...addressForm, land_mark: v})} />
                                      <div className="flex flex-col gap-1">
                                        <label className="text-[10px] font-black text-purple-400 uppercase tracking-widest ml-1 text-left">Type (e.g. Home, Work)</label>
                                        <input 
                                          className={`w-full bg-purple-50/30 border ${typeError ? 'border-red-400' : 'border-purple-100'} rounded-xl p-3 text-sm text-purple-950 font-bold outline-none focus:ring-2 ring-pink-200 transition-all`} 
                                          placeholder="Enter address name"
                                          value={addressForm.address_type} 
                                          onChange={(e) => handleTypeChange(e.target.value)} 
                                          required 
                                        />
                                        {typeError && (
                                          <span className="text-[9px] text-red-500 font-bold ml-1 text-left">{typeError}</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                      <FormInput label="City" value={addressForm.city} onChange={(v) => setAddressForm({...addressForm, city: v})} required />
                                      <FormInput label="State" value={addressForm.state} onChange={(v) => setAddressForm({...addressForm, state: v})} required />
                                      <FormInput label="Pincode" maxLength={6} value={addressForm.pincode} onChange={(v) => setAddressForm({...addressForm, pincode: v.replace(/\D/g,"")})} required />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                      <label className="text-[10px] font-black text-purple-400 uppercase tracking-widest ml-1 text-left">Full Address</label>
                                      <textarea rows="2" className="w-full bg-purple-50/30 border border-purple-100 rounded-xl p-3 text-sm text-purple-950 font-bold outline-none" value={addressForm.address} onChange={(e) => setAddressForm({...addressForm, address: e.target.value})} />
                                    </div>
                                    <button type="submit" disabled={!!typeError} className={`w-full ${typeError ? 'bg-gray-300' : 'bg-purple-950'} text-white py-4 rounded-xl font-black uppercase text-xs tracking-widest shadow-lg transition-all`}>
                                      {editMode ? "Update Address" : "Save Address"}
                                    </button>
                                </form>
                            )
                        ) : (
                            <div className="p-8 border-2 border-dashed border-gray-200 rounded-3xl text-center text-gray-400 text-sm font-bold uppercase italic">Shipping to personal details provided previously</div>
                        )
                    )}
                </div>

                <div className="mb-8">
                  <h4 className="text-sm font-black text-purple-950 uppercase mb-4">Payment Method</h4>
                  <div className="p-4 rounded-3xl border-2 border-pink-500 bg-pink-50 flex items-center gap-4">
                    <div className="w-6 h-6 rounded-full border-2 border-pink-500 flex items-center justify-center">
                        <div className="w-3 h-3 bg-pink-500 rounded-full" />
                    </div>
                    <div>
                      <p className="text-sm font-black text-purple-950 uppercase">Online Payment Only</p>
                      <p className="text-[10px] text-pink-600 font-bold tracking-widest">UPI, CARDS, NETBANKING</p>
                    </div>
                  </div>
                </div>

                <button disabled={(distributionType === "self" && !isAnonymous && !selectedAddressId) || isSubmitting} onClick={handleSakhiOrderCreate} className="w-full bg-pink-500 text-white py-5 rounded-[2rem] font-black text-xl shadow-xl shadow-pink-100 flex items-center justify-center gap-3 transition-all active:scale-95 disabled:bg-gray-200">
                  {isSubmitting ? <Loader2 className="animate-spin" /> : `Confirm Order • ${selectedTier.price}`}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <nav className="p-6 max-w-7xl mx-auto flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-white rounded-full transition-colors border border-transparent hover:border-pink-200"><ArrowLeft size={24} /></button>
        <h1 className="text-xl font-black uppercase tracking-tighter text-purple-950">Checkout <span className="text-pink-600 italic">Confirmation</span></h1>
      </nav>

      <main className="max-w-6xl mx-auto px-6 mt-8">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <section>
              <h2 className="text-2xl font-black text-purple-950 mb-2 uppercase italic">How should we distribute?</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <button disabled={isSubmitting} onClick={() => setDistributionType("eveya")} className={`text-left p-6 rounded-[2.5rem] border-4 transition-all duration-300 relative overflow-hidden ${distributionType === "eveya" ? "bg-white border-purple-800 shadow-2xl scale-[1.02]" : "bg-white/50 border-transparent hover:border-purple-200"}`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${distributionType === 'eveya' ? 'bg-purple-800 text-white' : 'bg-purple-100 text-purple-800'}`}><Building2 size={28} /></div>
                  <h3 className="text-xl font-black text-purple-950 mb-2">Through Eveya</h3>
                  <p className="text-sm text-slate-500 font-medium leading-relaxed">We will identify beneficiaries in rural areas and handle the entire logistics network.</p>
                  {distributionType === "eveya" && <CheckCircle2 className="absolute top-6 right-6 text-purple-800" fill="white" />}
                </button>
                <button disabled={isSubmitting} onClick={() => setDistributionType("self")} className={`text-left p-6 rounded-[2.5rem] border-4 transition-all duration-300 relative overflow-hidden ${distributionType === "self" ? "bg-white border-pink-500 shadow-2xl scale-[1.02]" : "bg-white/50 border-transparent hover:border-pink-200"}`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${distributionType === 'self' ? 'bg-pink-500 text-white' : 'bg-pink-100 text-pink-500'}`}><Package size={28} /></div>
                  <h3 className="text-xl font-black text-purple-950 mb-2">Self Managed</h3>
                  <p className="text-sm text-slate-500 font-medium leading-relaxed">We ship the kits to your address, and you handle the distribution to women in your community.</p>
                  {distributionType === "self" && <CheckCircle2 className="absolute top-6 right-6 text-pink-500" fill="white" />}
                </button>
              </div>
            </section>
            <div className="bg-white/60 p-8 rounded-[2.5rem] border border-white">
              <h3 className="font-black text-purple-950 uppercase tracking-tighter mb-4 flex items-center gap-2"><ShieldCheck className="text-purple-800" /> Supporter Profile</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-[10px] font-black text-slate-400 uppercase">Identity</p><p className="font-bold text-slate-700">{isAnonymous ? "Anonymous Benefactor" : userDetails.name}</p></div>
                {!isAnonymous && (<div><p className="text-[10px] font-black text-slate-400 uppercase">Contact</p><p className="font-bold text-slate-700">{userDetails.mobile}</p></div>)}
              </div>
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="bg-purple-950 text-white p-8 rounded-[3rem] sticky top-8 shadow-2xl">
              <h3 className="text-2xl font-black mb-6 uppercase tracking-tighter italic">Summary</h3>
              <div className="space-y-4 mb-8">
                <div className="flex justify-between items-center"><span className="text-purple-300 font-bold uppercase text-xs">Plan</span><span className="font-black">{selectedTier.tier}</span></div>
                <div className="flex justify-between items-center"><span className="text-purple-300 font-bold uppercase text-xs">Pads</span><span className="font-black">{selectedTier.pads}</span></div>
                <div className="flex justify-between items-center"><span className="text-purple-300 font-bold uppercase text-xs">Method</span><span className="font-black text-pink-400 uppercase text-[10px]">{distributionType}</span></div>
              </div>
              <div className="h-px bg-purple-800 w-full mb-6"></div>
              <div className="flex justify-between items-end mb-8">
                <div><p className="text-purple-300 font-black text-[10px] uppercase">Contribution</p><p className="text-4xl font-black tracking-tighter leading-none">{selectedTier.price}</p></div>
                <Heart className="inline text-pink-500" fill="currentColor" size={20} />
              </div>
              <button disabled={isSubmitting} onClick={() => setShowModal(true)} className="w-full bg-pink-500 hover:bg-pink-600 disabled:bg-slate-600 text-white py-5 rounded-2xl font-black text-lg shadow-xl flex items-center justify-center gap-3 transition-all active:scale-95">
                 Proceed to Checkout <CreditCard size={20} />
              </button>
            </div>
          </div>
        </div>
      </main>
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d8b4fe; border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default SakhiCheckout;